import { useMap } from '@vis.gl/react-google-maps';
import type { PointerEvent, PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props extends PropsWithChildren {
  /** Coordinates supplied by the parent; drag callbacks let the parent update them. */
  latitude: number;
  longitude: number;
  selected?: boolean;
  /** Override the overlay's stacking order when marker types have different priorities. */
  zIndex?: number;
  draggable?: boolean;
  /** Called on pointer down, before movement, so the parent can pause camera synchronization. */
  onDragStart?: () => void;
  /** Reports the current coordinates once movement exceeds the click threshold. */
  onDrag?: (latitude: number, longitude: number) => void;
  /** Reports the final coordinates only when a moved marker is released normally. */
  onDragEnd?: (latitude: number, longitude: number) => void;
  /** Ends the gesture after cancellation or a click without movement; does not undo onDrag updates. */
  onDragCancel?: () => void;
}

interface MarkerDrag {
  pointerId: number;
  clientX: number;
  clientY: number;
  /** Geographic anchor projected into pixels relative to the map container at pointer down. */
  origin: google.maps.Point;
  position: google.maps.LatLngLiteral;
  moved: boolean;
}

/**
 * Places React content on a native OverlayView inside a vis.gl Map.
 * Google Maps owns the DOM attachment and positioning; React owns the marker content.
 * This preserves existing CustomMarker CSS anchors without requiring a cloud Map ID.
 * The parent owns the coordinates and should persist changes through onDrag or onDragEnd.
 */
const GoogleMapsHtmlMarker = ({
  latitude,
  longitude,
  selected,
  zIndex,
  draggable = false,
  onDragStart,
  onDrag,
  onDragEnd,
  onDragCancel,
  children
}: Props) => {
  const map = useMap();
  // Keep one portal target across renders, including when the native overlay is recreated.
  const [element] = useState(() => document.createElement('div'));
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  // Native draw() reads current values through refs without recreating the overlay or
  // requiring a React render for every pointer movement.
  const positionRef = useRef({ lat: latitude, lng: longitude });
  const dragRef = useRef<MarkerDrag | null>(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    if (!map) return;

    // Define the subclass only after the shared API loader has finished.
    class HtmlOverlay extends google.maps.OverlayView {
      onAdd() {
        element.style.position = 'absolute';
        // The zero-sized root marks the exact geographic anchor. CustomMarker CSS
        // supplies the visual offset so the pin's tip stays on that point.
        element.style.width = '0';
        element.style.height = '0';
        // This pane accepts pointer events. Marker gestures must not also pan the map
        // or trigger its click handler; events still reach the React marker children.
        this.getPanes()?.overlayMouseTarget.appendChild(element);
        google.maps.OverlayView.preventMapHitsAndGesturesFrom(element);
      }

      draw() {
        // Google Maps also calls draw() when its camera changes. Pane-relative pixels
        // position the overlay; dragging below uses container-relative pixels instead.
        const position = dragRef.current?.position ?? positionRef.current;
        const point = this.getProjection()?.fromLatLngToDivPixel(new google.maps.LatLng(position));
        if (!point) return;
        element.style.left = `${point.x}px`;
        element.style.top = `${point.y}px`;
      }

      onRemove() {
        element.remove();
      }
    }

    const overlay = new HtmlOverlay();
    overlayRef.current = overlay;
    overlay.setMap(map);
    return () => {
      // Detaching invokes onRemove(); React handles removal of the portal's children.
      overlay.setMap(null);
      overlayRef.current = null;
      dragRef.current = null;
    };
  }, [map, element]);

  useEffect(() => {
    // Accept form edits and resets. During a gesture, draw() prioritizes the drag position.
    positionRef.current = { lat: latitude, lng: longitude };
    overlayRef.current?.draw();
  }, [latitude, longitude]);

  useEffect(() => {
    // Raise the overlay itself: the child's z-index cannot reorder separate overlay roots.
    element.style.zIndex = String(zIndex ?? (selected ? 4 : 2));
  }, [element, selected, zIndex]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    suppressClick.current = false;
    if (!draggable || event.button !== 0 || dragRef.current) return;
    const projection = overlayRef.current?.getProjection();
    const origin = projection?.fromLatLngToContainerPixel(
      new google.maps.LatLng(positionRef.current)
    );
    if (!origin) return;
    event.preventDefault();
    // Continue receiving move/up events even after the pointer leaves the marker.
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      origin,
      position: positionRef.current,
      moved: false
    };
    onDragStart?.();
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.clientX;
    const dy = event.clientY - drag.clientY;
    // Small involuntary movements should still allow the marker's normal click action.
    if (!drag.moved && Math.hypot(dx, dy) < 3) return;
    // Add the pointer delta to the projected anchor, preserving where the marker was
    // grabbed. Viewport and container coordinates have the same pixel deltas, so no
    // map bounding-rectangle offset is needed here.
    const position = overlayRef.current
      ?.getProjection()
      ?.fromContainerPixelToLatLng(new google.maps.Point(drag.origin.x + dx, drag.origin.y + dy));
    if (!position) return;
    drag.moved = true;
    drag.position = position.toJSON();
    overlayRef.current?.draw();
    onDrag?.(drag.position.lat, drag.position.lng);
  };

  const finishDrag = (event: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    // Clear first: releasing capture also emits lostpointercapture, which must not
    // finish the same gesture again or report a successful drop as cancelled.
    dragRef.current = null;
    suppressClick.current = drag.moved;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (cancelled || !drag.moved) {
      // Resume drawing from the latest parent coordinates. Already emitted onDrag
      // updates remain valid; cancellation does not restore the pointer-down position.
      overlayRef.current?.draw();
      onDragCancel?.();
      return;
    }
    // Keep the released position until the form supplies the updated coordinates.
    positionRef.current = drag.position;
    onDragEnd?.(drag.position.lat, drag.position.lng);
  };

  // The portal preserves React context and child event handlers inside the Google Maps
  // pane. touchAction prevents browser touch gestures from interrupting marker dragging.
  return createPortal(
    <div
      data-map-marker
      style={{ touchAction: draggable ? 'none' : undefined }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => finishDrag(event, false)}
      onPointerCancel={(event) => finishDrag(event, true)}
      onLostPointerCapture={(event) => finishDrag(event, true)}
      onClickCapture={(event) => {
        // Browsers may emit click after pointer up; a completed drag must not select
        // the marker or open its popover. A gesture without movement still clicks normally.
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.stopPropagation();
      }}
    >
      {children}
    </div>,
    element
  );
};

export default GoogleMapsHtmlMarker;
